// frontend/src/modules/projects/pages/TimesheetApprovalsPage.tsx
//
// The manager-facing review queue for tracked time (migration 077).
//
// The whole feature is OPT-IN: `tenant_settings.require_timesheet_approval`
// defaults FALSE, and while it is off every logged hour is already 'approved'
// and flows straight onto a client invoice. So this screen does two jobs:
//   1. says plainly whether review is ON, and lets a settings.write holder
//      turn it on/off (PATCH /api/tenant/settings — there is no projects route
//      for the flag);
//   2. lists entries by approval status and approves / rejects / sends back
//      one or many of them.
//
// Server contract (api/modules/projects/routes.ts):
//   GET  /api/projects/time-entries/approval-required   time_entries.view_self
//        → { require_timesheet_approval: boolean }
//   GET  /api/projects/time-entries?approval_status=…   time_entries.view_all
//        → { rows: TimeEntry[] }   (raw rows — no employee/project join)
//   POST /api/projects/time-entries/{approve,reject,reopen}   time_entries.approve
//        body { entry_ids: string[], note?: string } → { decision, count, entries }
//
// Two refusals are surfaced verbatim rather than as a generic failure, because
// each names its own fix:
//   · 409 `entry_invoiced`          — cancel the invoice and release its time
//   · 403 `own_time_entry_approval` — a non-owner may not decide their own hours
// The second is also pre-empted in the UI: your own rows are marked and
// excluded from a bulk decision, exactly like the server guard (owners exempt).

import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ClipboardCheck, ShieldCheck, ShieldOff, Check, X, Undo2 } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { NoAccessState } from '../../../design-system/components/NoAccessState';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { Button } from '../../../components/ui/button';
import { useConfirm, usePrompt } from '../../../components/ui/confirm-dialog';
import { useApi } from '../../../design-system/useApi';
import { useAuth } from '../../../core/auth/AuthProvider';
import { usePermission } from '../../../core/auth/usePermission';
import { api } from '../../../core/api-client';
import { formatDateTime } from '../../../design-system/format';
import { VISUAL, statusPill, statusDot } from '../../../design-system/visual';
import { GlassCard, TabStrip } from '../../../design-system/pds';
import { projectsApi } from '../api';
import type { Project, TimeEntry, TimesheetApprovalStatus } from '../types';
import type { EmployeeWithUser } from '../../hr/types';

const TABS: Array<{ id: TimesheetApprovalStatus; label: string }> = [
  { id: 'pending',  label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
];

const STATUS_TONE = {
  pending:  'warning',
  approved: 'success',
  rejected: 'destructive',
} as const;

function hours(entry: TimeEntry): string {
  if (!entry.end_at) return '—';
  const ms = new Date(entry.end_at).getTime() - new Date(entry.start_at).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  return `${(ms / 3_600_000).toFixed(2)}h`;
}

export function TimesheetApprovalsPage() {
  const { identity } = useAuth();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const canApprove = usePermission('time_entries.approve');
  const canWriteSettings = usePermission('settings.write');
  const isOwner = identity?.type === 'user' && identity.is_owner === true;

  const [tab, setTab] = React.useState<TimesheetApprovalStatus>('pending');
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [busy, setBusy] = React.useState<TimesheetApprovalStatus | null>(null);
  const [togglingPolicy, setTogglingPolicy] = React.useState(false);

  const entriesQ = useApi<{ rows: TimeEntry[] }>(`/api/projects/time-entries?approval_status=${tab}`, [tab]);
  const policyQ = useApi<{ require_timesheet_approval: boolean }>('/api/projects/time-entries/approval-required');
  const projectsQ = useApi<{ projects: Project[] }>('/api/projects/projects');
  const employeesQ = useApi<{ employees: EmployeeWithUser[] }>('/api/hr/employees');

  const rows = entriesQ.data?.rows ?? [];
  const required = policyQ.data?.require_timesheet_approval === true;

  const projectName = React.useMemo(() => {
    const m = new Map<string, string>();
    (projectsQ.data?.projects ?? []).forEach((p) => m.set(p.id, p.name));
    return m;
  }, [projectsQ.data]);

  const employees = employeesQ.data?.employees ?? [];
  const employeeName = React.useMemo(() => {
    const m = new Map<string, string>();
    employees.forEach((e) => m.set(e.id, e.name));
    return m;
  }, [employees]);

  // Mirror of the server's self-approval guard: a non-owner may not decide
  // their own hours. Owners are exempt (there is nobody above them).
  const ownEmployeeId = React.useMemo(() => {
    if (identity?.type !== 'user') return null;
    return employees.find((e) => e.user_id === identity.id)?.id ?? null;
  }, [employees, identity]);
  const isOwnRow = React.useCallback(
    (entry: TimeEntry) => !isOwner && ownEmployeeId != null && entry.employee_id === ownEmployeeId,
    [isOwner, ownEmployeeId],
  );

  // Rows the caller could actually act on — the "select all" box and every
  // bulk decision work off this, never off the raw list.
  const decidable = React.useMemo(() => rows.filter((r) => !isOwnRow(r)), [rows, isOwnRow]);

  React.useEffect(() => { setSelected(new Set()); }, [tab]);

  function toggleRow(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleAll() {
    setSelected((s) => (s.size === decidable.length ? new Set() : new Set(decidable.map((r) => r.id))));
  }

  async function togglePolicy() {
    const turningOn = !required;
    const ok = await confirm({
      title: turningOn ? 'Require timesheet approval?' : 'Turn timesheet approval off?',
      description: turningOn
        ? 'New time entries will start as Pending and cannot be put on a client invoice until an approver signs them off. Time already logged keeps whatever status it has.'
        : 'New time entries will be approved on creation and can be invoiced straight away. Entries already sitting in Pending stay pending until someone decides them.',
      confirmLabel: turningOn ? 'Require approval' : 'Turn off',
    });
    if (!ok) return;
    setTogglingPolicy(true);
    try {
      await api('/api/tenant/settings', { method: 'PATCH', body: { require_timesheet_approval: turningOn } });
      toast.success(turningOn ? 'Timesheet approval is on' : 'Timesheet approval is off');
      policyQ.reload();
    } catch (err: any) {
      toast.error('Could not change the setting', { description: err.message ?? String(err) });
    } finally {
      setTogglingPolicy(false);
    }
  }

  async function decide(decision: TimesheetApprovalStatus) {
    const ids = decidable.filter((r) => selected.has(r.id)).map((r) => r.id);
    if (ids.length === 0) {
      toast.error('Select at least one entry');
      return;
    }
    let note: string | null = null;
    if (decision === 'rejected') {
      note = await prompt({
        title: `Reject ${ids.length} time entr${ids.length === 1 ? 'y' : 'ies'}?`,
        description: 'The reason is stored on the entry so the person who logged the time can see why.',
        placeholder: 'e.g. Logged against the wrong project',
        confirmLabel: 'Reject',
        destructive: true,
      });
      if (note == null) return;
    }
    setBusy(decision);
    try {
      const res = await projectsApi.decideTimeEntries(decision, ids, note);
      // `count` is what was actually written. It can be lower than the request
      // if an entry was invoiced between the read and the write, so report the
      // server's number, never the optimistic one.
      const verb = decision === 'approved' ? 'Approved' : decision === 'rejected' ? 'Rejected' : 'Sent back for review';
      if (res.count === 0) {
        toast.warning('Nothing changed', { description: 'None of the selected entries could be updated.' });
      } else {
        toast.success(`${verb} ${res.count} entr${res.count === 1 ? 'y' : 'ies'}`);
      }
      setSelected(new Set());
      entriesQ.reload();
    } catch (err: any) {
      if (err?.code === 'entry_invoiced') {
        toast.error('Already invoiced', { description: err.message });
      } else if (err?.code === 'own_time_entry_approval') {
        toast.error('You cannot decide your own hours', { description: err.message });
      } else {
        toast.error('Decision failed', { description: err?.message ?? String(err) });
      }
    } finally {
      setBusy(null);
    }
  }

  const allSelected = decidable.length > 0 && selected.size === decidable.length;

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow={
          <>
            <Link to="/dashboard/projects" className="hover:text-foreground transition-colors">Projects</Link>
            {' / Timesheets'}
          </>
        }
        title="Timesheet approvals"
        description="Review tracked time before it can be put on a client invoice. Approving is not final — an approved entry can be sent back until the hours are actually billed."
        actions={
          canWriteSettings ? (
            <Button
              variant="outline"
              className="rounded-full h-10 gap-1.5"
              disabled={togglingPolicy || policyQ.loading}
              onClick={togglePolicy}
            >
              {required ? <ShieldOff className="h-3.5 w-3.5" /> : <ShieldCheck className="h-3.5 w-3.5" />}
              {togglingPolicy ? 'Saving…' : required ? 'Turn approval off' : 'Require approval'}
            </Button>
          ) : undefined
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-5">
        {/* The single most important fact on the page: is review actually on? */}
        {!policyQ.loading && !policyQ.error && !required && (
          <section
            role="status"
            className="flex items-start gap-3 rounded-2xl px-5 py-4 ring-1 ring-amber-500/30 bg-amber-500/10 dark:bg-amber-400/10 dark:ring-amber-400/30"
          >
            <ShieldOff className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="min-w-0">
              <p className="text-[13.5px] font-semibold text-amber-800 dark:text-amber-200">
                Timesheet approval is off for this workspace
              </p>
              <p className="text-[12.5px] text-amber-800/80 dark:text-amber-200/75 mt-1">
                Every hour logged is approved the moment it is saved and can go straight onto a client invoice.
                {canWriteSettings
                  ? ' Turn approval on to review time first — anything already logged keeps its current status.'
                  : ' A workspace owner can turn review on from Settings.'}
              </p>
            </div>
          </section>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <TabStrip tabs={TABS} active={tab} onChange={setTab} />
          {canApprove && selected.size > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-[12.5px] font-semibold text-ink/60 dark:text-white/60">
                {selected.size} selected
              </span>
              {tab !== 'approved' && (
                <Button size="sm" className="rounded-full gap-1.5" disabled={busy !== null} onClick={() => decide('approved')}>
                  <Check className="h-3.5 w-3.5" /> {busy === 'approved' ? 'Approving…' : 'Approve'}
                </Button>
              )}
              {tab !== 'rejected' && (
                <Button size="sm" variant="outline" className="rounded-full gap-1.5" disabled={busy !== null} onClick={() => decide('rejected')}>
                  <X className="h-3.5 w-3.5" /> {busy === 'rejected' ? 'Rejecting…' : 'Reject'}
                </Button>
              )}
              {tab !== 'pending' && (
                <Button size="sm" variant="ghost" className="rounded-full gap-1.5" disabled={busy !== null} onClick={() => decide('pending')}>
                  <Undo2 className="h-3.5 w-3.5" /> {busy === 'pending' ? 'Sending back…' : 'Send back'}
                </Button>
              )}
            </div>
          )}
        </div>

        <GlassCard padding="p-0" className="overflow-hidden">
          {entriesQ.loading ? (
            <div className="py-16 text-center text-[13px] text-ink/50 dark:text-white/50">Loading…</div>
          ) : entriesQ.status === 403 ? (
            <NoAccessState
              title="You can't view other people's time entries"
              description="Reviewing timesheets needs the time_entries.view_all permission as well as time_entries.approve. Ask an Owner to grant it from Settings → Access."
            />
          ) : entriesQ.error ? (
            <ErrorState title="Couldn't load timesheets" description={entriesQ.error} onRetry={entriesQ.reload} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<ClipboardCheck />}
              title={tab === 'pending' ? 'Nothing waiting on you' : `No ${tab} entries`}
              description={
                tab === 'pending'
                  ? 'Time entries appear here as soon as somebody logs hours that need review.'
                  : 'Switch tabs to see time in another state.'
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-separate border-spacing-0">
                <thead>
                  <tr className="text-[10.5px] uppercase tracking-[0.14em] text-ink/55 dark:text-white/55">
                    <th className="text-left py-3 pl-5 pr-2 font-semibold w-10">
                      {canApprove && decidable.length > 0 && (
                        <input
                          type="checkbox"
                          aria-label="Select all entries"
                          checked={allSelected}
                          onChange={toggleAll}
                          className="h-4 w-4 rounded border-border accent-saffron"
                        />
                      )}
                    </th>
                    <th className="text-left py-3 px-4 font-semibold">Employee</th>
                    <th className="text-left py-3 px-4 font-semibold">Project</th>
                    <th className="text-left py-3 px-4 font-semibold">Started</th>
                    <th className="text-right py-3 px-4 font-semibold">Hours</th>
                    <th className="text-left py-3 px-4 font-semibold">Note</th>
                    <th className="text-right py-3 px-5 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const own = isOwnRow(r);
                    const status = r.approval_status ?? 'approved';
                    return (
                      <tr key={r.id} className="border-t border-ink/6 dark:border-white/8">
                        <td className="py-3.5 pl-5 pr-2">
                          {canApprove && !own && (
                            <input
                              type="checkbox"
                              aria-label={`Select entry ${r.id}`}
                              checked={selected.has(r.id)}
                              onChange={() => toggleRow(r.id)}
                              className="h-4 w-4 rounded border-border accent-saffron"
                            />
                          )}
                        </td>
                        <td className="py-3.5 px-4 text-[13px] text-ink dark:text-white">
                          {employeeName.get(r.employee_id) ?? r.employee_id.slice(0, 8)}
                          {own && (
                            <span className="block text-[11.5px] text-ink/50 dark:text-white/50 mt-0.5">
                              Your own hours — another approver must review these
                            </span>
                          )}
                        </td>
                        <td className="py-3.5 px-4 text-[13px] text-ink/75 dark:text-white/75">
                          {projectName.get(r.project_id) ?? r.project_id.slice(0, 8)}
                        </td>
                        <td className="py-3.5 px-4 text-[13px] text-ink/75 dark:text-white/75 whitespace-nowrap">
                          {formatDateTime(r.start_at)}
                        </td>
                        <td className="py-3.5 px-4 text-right text-[13px] font-mono tabular-nums text-ink dark:text-white">
                          {hours(r)}
                        </td>
                        <td className="py-3.5 px-4 text-[12.5px] text-ink/60 dark:text-white/60 max-w-[240px] truncate">
                          {status === 'rejected' && r.rejection_note ? r.rejection_note : (r.notes ?? '—')}
                        </td>
                        <td className="py-3.5 px-5 text-right">
                          <span className={statusPill(STATUS_TONE[status])}>
                            <span className={statusDot(STATUS_TONE[status])} />
                            {status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </GlassCard>

        <p className={[VISUAL.eyebrow, 'block'].join(' ')}>
          Invoiced time is frozen — release it from its cancelled invoice before changing the approval.
        </p>
      </div>
    </div>
  );
}
